import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, MessageSquare, Calendar, Bell, DollarSign, ExternalLink, FileText, Mail, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMSOptionsModal = ({ job, isOpen, onClose, mode = 'sms', onSuccess }) => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  
  if (!isOpen) return null;

  const businessName = user?.business_name || user?.name || 'Your Business';
  const businessPhone = user?.business_phone || '';
  const businessEmail = user?.business_email || '';
  const customerPortalUrl = `${window.location.origin}/portal/${job.customer_token}`;
  const isEmail = mode === 'email';

  // Calculate price breakdown
  const laborAmount = job.labor_amount || 0;
  const materialsAmount = job.materials_amount || 0;
  const subtotal = laborAmount + materialsAmount;
  const taxAmount = job.tax_amount || 0;
  const taxBreakdown = job.tax_breakdown || [];
  const total = job.quote_amount || subtotal + taxAmount;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const [hour, min] = timeStr.split(':').map(Number);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Build price breakdown string
  const getPriceBreakdown = () => {
    let breakdown = '';
    if (laborAmount > 0) breakdown += `Labor: $${laborAmount.toFixed(2)}\n`;
    if (materialsAmount > 0) breakdown += `Materials: $${materialsAmount.toFixed(2)}\n`;
    if (laborAmount > 0 || materialsAmount > 0) breakdown += `Subtotal: $${subtotal.toFixed(2)}\n`;
    if (taxBreakdown.length > 0) {
      taxBreakdown.forEach(tax => {
        breakdown += `${tax.name} (${tax.rate}%): $${tax.amount.toFixed(2)}\n`;
      });
    } else if (taxAmount > 0) {
      breakdown += `Tax: $${taxAmount.toFixed(2)}\n`;
    }
    breakdown += `\nTOTAL: $${total.toFixed(2)}`;
    return breakdown;
  };

  // Build business signature
  const getBusinessSignature = () => {
    let sig = `\n\n---\n${businessName}`;
    if (businessPhone) sig += `\nPhone: ${businessPhone}`;
    if (businessEmail) sig += `\nEmail: ${businessEmail}`;
    return sig;
  };

  const messageOptions = [
    {
      id: 'quote',
      icon: FileText,
      title: 'Send Quote',
      description: 'Send job quote with price breakdown',
      color: 'bg-purple-500',
      getSubject: () => `Quote from ${businessName}`,
      getMessage: () => {
        return `Hi ${job.client_name}!\n\nHere's your quote from ${businessName}:\n\n${job.description}\n\n${getPriceBreakdown()}\n\nView full details: ${customerPortalUrl}\n\nReply to confirm or with any questions!${getBusinessSignature()}`;
      }
    },
    {
      id: 'booking',
      icon: Calendar,
      title: 'Booking Confirmation',
      description: 'Confirm the appointment',
      color: 'bg-blue-500',
      getSubject: () => `Appointment Confirmed - ${businessName}`,
      getMessage: () => {
        const dateTime = job.scheduled_date 
          ? `${formatDate(job.scheduled_date)}${job.scheduled_time ? ` at ${formatTime(job.scheduled_time)}` : ''}`
          : 'your scheduled date';
        return `Hi ${job.client_name}!\n\nYour appointment with ${businessName} is confirmed for ${dateTime}.${job.full_address || job.address ? `\n\nAddress: ${job.full_address || job.address}` : ''}\n\n${getPriceBreakdown()}\n\nView details: ${customerPortalUrl}\n\nSee you soon!${getBusinessSignature()}`;
      }
    },
    {
      id: 'reminder',
      icon: Bell,
      title: 'Appointment Reminder',
      description: 'Remind about upcoming appointment',
      color: 'bg-amber-500',
      getSubject: () => `Reminder: Upcoming Appointment - ${businessName}`,
      getMessage: () => {
        const dateTime = job.scheduled_date 
          ? `${formatDate(job.scheduled_date)}${job.scheduled_time ? ` at ${formatTime(job.scheduled_time)}` : ''}`
          : 'your scheduled date';
        return `Hi ${job.client_name}!\n\nJust a reminder about your appointment with ${businessName} on ${dateTime}.${job.full_address || job.address ? `\n\nAddress: ${job.full_address || job.address}` : ''}\n\nView details: ${customerPortalUrl}\n\nReply if you need to reschedule!${getBusinessSignature()}`;
      }
    },
    {
      id: 'payment',
      icon: DollarSign,
      title: 'Payment Request',
      description: 'Send invoice with payment link',
      color: 'bg-emerald-500',
      getSubject: () => `Invoice from ${businessName}`,
      getMessage: () => {
        return `Hi ${job.client_name}!\n\nThis is ${businessName}. Your invoice for "${job.description}" is ready.\n\n${getPriceBreakdown()}\n\nView details & pay securely: ${customerPortalUrl}\n\nThank you for your business!${getBusinessSignature()}`;
      }
    }
  ];

  const handleSend = async (option) => {
    if (isEmail) {
      if (!job.client_email) {
        toast.error('No email address for this client');
        return;
      }
      
      setSending(true);
      try {
        // Call backend to send email (handles native vs mailto based on plan)
        // Map frontend option IDs to backend reminder types
        const reminderTypeMap = {
          'quote': 'quote',
          'booking': 'appointment',
          'reminder': 'appointment',
          'payment': 'payment'
        };
        const response = await axios.post(`${API}/email/reminder`, {
          job_id: job.id,
          reminder_type: reminderTypeMap[option.id] || option.id
        });
        
        if (response.data.limit_reached) {
          // Monthly limit reached
          toast.error(response.data.message);
        } else if (response.data.use_mailto) {
          // Plan doesn't have native email - use mailto
          window.location.href = response.data.mailto_link;
          toast.success(`Opening email to ${job.client_email}`);
        } else if (response.data.demo_mode) {
          // Demo mode - show simulated message
          toast.success(`Demo: Email simulated to ${job.client_email}`);
        } else {
          // Email sent via Resend
          toast.success(`Email sent to ${job.client_email}`);
        }
        onClose();
        // Call onSuccess to refresh job data (status may have changed to 'invoiced')
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error('Email send error:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to send email';
        toast.error(errorMessage);
      } finally {
        setSending(false);
      }
    } else {
      // SMS
      if (!job.client_phone) {
        toast.error('No phone number for this client');
        return;
      }
      
      setSending(true);
      try {
        // Call backend to send SMS (handles Twilio vs native based on plan)
        const response = await axios.post(`${API}/messaging/sms/send`, {
          to: job.client_phone,
          message: option.getMessage()
        });
        
        if (response.data.limit_reached) {
          // Monthly limit reached
          toast.error(response.data.message);
        } else if (response.data.use_native) {
          // Plan doesn't have Twilio - use native SMS app
          window.location.href = response.data.sms_link;
          toast.success('Opening SMS app');
        } else if (response.data.demo_mode) {
          // Demo mode - show simulated message
          toast.success(`Demo: SMS simulated to ${job.client_phone}`);
        } else {
          // SMS sent via Twilio
          toast.success(`SMS sent to ${job.client_phone}`);
        }
        onClose();
      } catch (error) {
        console.error('SMS send error:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to send SMS';
        toast.error(errorMessage);
      } finally {
        setSending(false);
      }
    }
  };

  const copyPortalLink = () => {
    navigator.clipboard.writeText(customerPortalUrl);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            {isEmail ? (
              <Mail className="w-5 h-5 text-[#FF5F00]" />
            ) : (
              <MessageSquare className="w-5 h-5 text-[#FF5F00]" />
            )}
            <h2 className="font-heading text-xl font-bold text-slate-900">
              {isEmail ? 'Send Email' : 'Send SMS'}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-message-modal"
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {/* Customer Portal Link */}
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <p className="text-slate-500 text-xs uppercase font-bold mb-1">Customer Portal Link</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={customerPortalUrl}
                className="flex-1 text-sm text-slate-700 bg-transparent truncate"
              />
              <button
                onClick={copyPortalLink}
                className="text-[#FF5F00] text-sm font-bold hover:underline flex-shrink-0"
              >
                Copy
              </button>
            </div>
          </div>

          <p className="text-slate-500 text-sm mb-4">
            Choose a message to send to {job.client_name}:
          </p>

          {/* Message Options */}
          <div className="space-y-3">
            {messageOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSend(option)}
                  disabled={sending}
                  data-testid={`${mode}-option-${option.id}`}
                  className="w-full p-4 bg-white border-2 border-slate-200 rounded-xl hover:border-[#FF5F00] transition-colors text-left flex items-start gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className={`w-10 h-10 ${option.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    {sending ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Icon className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 group-hover:text-[#FF5F00]">
                      {option.title}
                    </h3>
                    <p className="text-slate-500 text-sm">{option.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                </button>
              );
            })}
          </div>

          {/* Contact Warning */}
          {isEmail && !job.client_email && (
            <p className="text-red-500 text-sm mt-4 text-center">
              ⚠️ No email address on file for this client
            </p>
          )}
          {!isEmail && !job.client_phone && (
            <p className="text-red-500 text-sm mt-4 text-center">
              ⚠️ No phone number on file for this client
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMSOptionsModal;
